import { useEffect, useState } from "react";

// Check if localStorage is available
const isLocalStorageAvailable = (): boolean => {
  try {
    const testKey = "__storage_test__";
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};

// Safe wrapper for localStorage operations
export const safeStorage = {
  get: (key: string): any => {
    if (!isLocalStorageAvailable()) {
      return null;
    }
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (e) {
      console.warn(`Error reading from localStorage: ${e}`);
      return null;
    }
  },

  set: (key: string, value: any): boolean => {
    if (!isLocalStorageAvailable()) {
      return false;
    }
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (e) {
      console.warn(`Error writing to localStorage: ${e}`);
      return false;
    }
  },

  remove: (key: string): boolean => {
    if (!isLocalStorageAvailable()) {
      return false;
    }
    try {
      localStorage.removeItem(key);
      return true;
    } catch (e) {
      console.warn(`Error removing from localStorage: ${e}`);
      return false;
    }
  },

  clear: (): boolean => {
    if (!isLocalStorageAvailable()) {
      return false;
    }
    try {
      localStorage.clear();
      return true;
    } catch (e) {
      console.warn(`Error clearing localStorage: ${e}`);
      return false;
    }
  },
};

// React hook for using localStorage with state
export function useLocalStorage<T>(
  key: string,
  initialValue: T
): [T, (value: T) => void] {
  // Initialize state with value from localStorage or initialValue
  const [storedValue, setStoredValue] = useState<T>(() => {
    return safeStorage.get(key) ?? initialValue;
  });
  // Update localStorage when state changes
  useEffect(() => {
    safeStorage.set(key, storedValue);
  }, [key, storedValue]);

  return [storedValue, setStoredValue];
}
